// Packages
import React from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

// Components
import Icon from "../global/icon"
import ClientSideOnly from "@components/global/client-side-only"

const size = {
  tablet: 850,
  mobile: 600,
}

const above = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const NowWhatWrapper = styled.div`
  margin-top: 72px;
  background-color: var(--ice-grey);
`
const NowWhatContainer = styled.div`
  max-width: 856px;
  margin: 0 auto;
  padding: 0 48px;
  ${below.mobile`padding: 0 24px;`}
`
const NowWhatCard = styled.div`
  position: relative;
  transform-style: preserve-3d;
  background: white;
  border-radius: 2px;
  padding: 48px 72px 72px;
  transform: translateY(-72px);
  /* box-shadow: 0 -6px 32px -16px rgba(0, 0, 0, 0.1); */
  :after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateZ(-1px) translateX(-50%);
    top: 0;
    box-shadow: 0px 3px 32px rgba(0, 0, 0, 0.1);
    height: 92%;
    width: 94%;
  }

  ${below.tablet`
  padding: 40px 56px 56px;
  `}
  ${below.mobile`
  padding: 24px 24px 40px;
  `}
  h2 {
    font-family: "Playfair Display", serif;
    font-weight: 400;
    font-size: 36px;
    line-height: 44px;
    margin-bottom: 48px;
    border-bottom: 2px solid var(--alt-black);
    padding-bottom: 4px;
    color: var(--alt-black);
    ${below.mobile`
    margin-bottom:40px;
    `};
  }
`
const NextStepWrapper = styled.div`
  padding: 0 0 28px;
  width: 100%;

  &:last-of-type {
    padding-bottom: 0;
  }
  a {
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
    .icon-wrapper {
      transform: scale(1.375);
    }

    .get-more-info-arrow {
      svg {
        filter: brightness(0) saturate(100%) invert(15%) sepia(45%)
          saturate(4261%) hue-rotate(198deg) brightness(91%) contrast(99%);
      }
    }

    .take-action-arrow {
      svg {
        filter: brightness(0) saturate(100%) invert(20%) sepia(100%)
          saturate(2743%) hue-rotate(254deg) brightness(84%) contrast(111%);
      }
    }

    .get-more-information {
      color: #013a74;
    }

    .take-action {
      color: #6325e0;
    }
  }

  .icon-wrapper {
    transition: 0.3s ease-in-out;
    svg {
      stroke: black;
      width: 28px;
    }
  }
`
const Step = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  dl {
    margin: 0 0 0 24px;
    flex-grow: 1;
  }

  dt {
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    color: var(--grey);
    transition: 0.3s ease-in-out;
    ${above.mobile`
    font-size:14px;
    line-height:17.6px;
    `}

    ${above.tablet`
    font-size:16px;
    line-height:20.11px;
    margin-bottom:4px;
    `}
  }
  dd {
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    p {
      font-size: 22px;
      line-height: 28px;
      color: var(--alt-black);
      margin-bottom: 0;
      margin-right: 12px;
      ${below.tablet`
    font-size: 20px;
    line-height: 28px;
    `}

      ${below.mobile`
    font-size:16px;
    line-height:20px;
    `}
    }
  }
`
const TeamButton = styled.button`
  background-color: transparent;
  width: 100%;
  border: none;
  outline: none;
  color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  &:hover {
    text-decoration: none;
    .icon-wrapper {
      transform: scale(1.375);
      svg {
        stroke: #38b2ac;
      }
    }
    dt {
      color: #38b2ac;
    }
  }

  .icon-wrapper {
    transition: 0.3s ease-in-out;
    margin-right: 24px;
    ${below.mobile`margin-bottom: 4px;`}
    svg {
      stroke: black;
      width: 24px;
      ${below.tablet`
      width: 16px;
      `}
    }
  }
`

const NowWhat = props => {
  const takeActionMB =
    props.bankerData?.team && !props.bankerData?.apply ? (
      <TeamButton onClick={() => props.teamApplyToggle(true)}>
        <Step>
          <Icon icon="purple checkbox" />
          <dl>
            <dt className="take-action">Take action</dt>
            <dd>
              <p>Apply online</p>
              <div className="icon-wrapper take-action-arrow">
                <Icon icon="arrow-right-thin" />
              </div>
            </dd>
          </dl>
        </Step>
      </TeamButton>
    ) : (
      <a href={props.bankerData?.apply}>
        <Step>
          <Icon icon="purple checkbox" />
          <dl>
            <dt className="take-action">Take action</dt>
            <dd>
              <p>
                {props.bankerData?.team
                  ? `Apply online`
                  : `Apply online through my application`}
              </p>
              <div className="icon-wrapper take-action-arrow">
                <Icon icon="arrow-right-thin" />
              </div>
            </dd>
          </dl>
        </Step>
      </a>
    )
  return (
    <NowWhatWrapper className="now-what-wrapper">
      <NowWhatContainer>
        <NowWhatCard>
          <h2>Now what?</h2>
          <NextStepWrapper>
            <Link
              to={
                props.walledVersion === "mb"
                  ? props.bankerData
                    ? `/${props.bankerData?.slug}/contact/`
                    : `/contact/`
                  : `/search-mortgage-bankers/`
              }
            >
              <Step>
                <Icon
                  icon={
                    props.walledVersion === "mb"
                      ? "blue phone"
                      : "blue magnifying glass"
                  }
                />
                <dl>
                  <dt className="get-more-information">Get more information</dt>
                  <dd>
                    {props.walledVersion === "mb" ? (
                      <ClientSideOnly
                        placeholder={<p>Talk about this topic</p>}
                      >
                        {props.bankerData?.team ? (
                          <p>Talk to us about this topic</p>
                        ) : (
                          <p>Talk to me about this topic</p>
                        )}
                      </ClientSideOnly>
                    ) : (
                      <p>
                        <strong>Find</strong> a mortgage banker near me to talk
                        to
                      </p>
                    )}
                    <div className="icon-wrapper get-more-info-arrow">
                      <Icon icon="arrow-right-thin" />
                    </div>
                  </dd>
                </dl>
              </Step>
            </Link>
          </NextStepWrapper>

          <NextStepWrapper>
            {props.walledVersion === "mb" ? (
              <ClientSideOnly
                placeholder={
                  <a href={"https://apply.atlanticbay.com/"}>
                    <Step>
                      <Icon icon="purple checkbox" />
                      <dl>
                        <dt className="take-action">Take action</dt>
                        <dd>
                          <p>Apply online</p>
                          <div className="icon-wrapper take-action-arrow">
                            <Icon icon="arrow-right-thin" />
                          </div>
                        </dd>
                      </dl>
                    </Step>
                  </a>
                }
              >
                {takeActionMB}
              </ClientSideOnly>
            ) : (
              <Link to="/pre-qualify/">
                <Step>
                  <Icon icon="purple checkbox" />
                  <dl>
                    <dt className="take-action">Take action</dt>
                    <dd>
                      <p>
                        <strong>Check</strong> what you can pre-qualify for
                      </p>
                      <div className="icon-wrapper take-action-arrow">
                        <Icon icon="arrow-right-thin" />
                      </div>
                    </dd>
                  </dl>
                </Step>
              </Link>
            )}
          </NextStepWrapper>

          {/* props.CTAs ? (
            props.CTAs.map((item, index) => (
              <NextStep
                key={index}
                title={item.title}
                type={item.type}
                corporateText={item.corporateText}
                walledText={item.walledText}
                bankerData={props.bankerData}
              />
            ))
          ) : (
            <>
              <NextStep
                title="Get more information"
                linksTo="/search-mortgage-bankers/"
                walledLinkType="internal"
                walledLinksTo={
                  props.bankerData && `/${props.bankerData?.slug}/contact/`
                }
                corporateIcon="green magnifying glass"
                corporateText="Find a mortgage banker near me to talk to."
                walledIcon="green phone"
                walledText="Talk to me about this topic"
                bankerData={props.bankerData}
              />
              <NextStep
                title="Take action"
                walledLinkType="external"
                linksTo="/pre-qualify/"
                walledLinksTo={props.bankerData && props.bankerData?.applyLink}
                corporateIcon="yellow checkbox"
                corporateText="Check what you can pre-qualify for."
                walledIcon="blue checkbox"
                walledText="Apply online through my application"
                bankerData={props.bankerData}
              />
            </>
              ) */}
        </NowWhatCard>
      </NowWhatContainer>
    </NowWhatWrapper>
  )
}

export default NowWhat
